import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { OperativeUnitDto } from "./operative-unit.dto";
import { CreateOperativeUnitDto } from "./create-operative-unit.dto";
import { UpdateOperativeUnitDto } from "./update-operative-unit.dto";
import { IRequest } from "@/interfaces/IRequest";
import { OptionType } from "@/types/OptionType";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { merge } from "lodash";
import { OperativeUnitWithAllocationCostDto } from "@/core/features/operative-units/operative-unit-with-allocation-cost.dto";
import { OperativeUnitWithTotalAllocationCostDto } from "@/core/features/operative-units/operative-unit-with-total-allocation-cost.dto";
import { ArchivedEnum } from "@altertec_gparn/lib";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class OperativeUnitsResource extends ResourceBaseService<
  OperativeUnitDto,
  CreateOperativeUnitDto,
  UpdateOperativeUnitDto
> {
  protected EntityDto = OperativeUnitDto;
  protected CreateEntityDto = CreateOperativeUnitDto;
  protected UpdateEntityDto = UpdateOperativeUnitDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/operative-units");
  }

  archive(id: string): Promise<OperativeUnitDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/archive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  unarchive(id: string): Promise<OperativeUnitDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/unarchive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  async optionLoaderWithArchived(
    inputValue: string,
    includeArchived = false,
    customRequest?: IRequest,
    includeName = false
  ): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name", "code"] } : {};
    request.sortBy = "code";
    request.limit = 500;
    if (customRequest) merge(request, customRequest);
    request.filters = Object.assign(request.filters || {}, {
      archived: includeArchived ? ArchivedEnum.ALL : ArchivedEnum.ONLY_ACTIVE,
    });
    applyOptionLoaderPagination(request);
    const { data } = await this.findAll(request, null);

    return data.map((value) =>
      value.mapEntityToOptionType([value.toString() + (includeName ? ` - ${value.name}` : "")])
    );
  }

  async optionLoader(inputValue: string, customRequest?: IRequest, includeName = false): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name", "code"] } : {};
    request.sortBy = "code";
    request.limit = 500;
    if (customRequest) merge(request, customRequest);
    const { data } = await this.findAll(applyOptionLoaderPagination(request));

    return data.map((value) =>
      value.mapEntityToOptionType([value.toString() + (includeName ? ` - ${value.name}` : "")])
    );
  }

  async optionLoaderMineWithArchived(inputValue: string, includeArchived = false): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name", "code"] } : {};
    request.sortBy = "name";
    request.limit = 500;
    request.filters = Object.assign(request.filters || {}, {
      archived: includeArchived ? ArchivedEnum.ALL : ArchivedEnum.ONLY_ACTIVE,
    });

    const { data } = await this.findAllMine(applyOptionLoaderPagination(request));
    return data.map((value: OperativeUnitDto) => value.mapEntityToOptionType([value.toString()]));
  }

  async optionLoaderMine(inputValue: string): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name", "code"] } : {};
    request.sortBy = "name";
    request.limit = 500;
    const { data } = await this.findAllMine(applyOptionLoaderPagination(request));
    return data.map((value: OperativeUnitDto) => value.mapEntityToOptionType([value.toString()]));
  }

  async findAllMine(request: IRequest): Promise<FindManyResult<OperativeUnitDto>> {
    return this.apiHttpClient
      .request<OperativeUnitDto, FindManyResult<OperativeUnitDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/mine",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((findManyResult) => {
        findManyResult.data = plainToInstance(OperativeUnitDto, findManyResult.data);
        return findManyResult;
      });
  }

  async findProvidingResourcesOperativeUnit(
    request: IRequest
  ): Promise<FindManyResult<OperativeUnitWithAllocationCostDto>> {
    return this.apiHttpClient
      .request<null, FindManyResult<OperativeUnitWithAllocationCostDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/providing-resources",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => {
        result.data = plainToInstance(OperativeUnitWithAllocationCostDto, result.data);
        return result;
      });
  }

  async findProvidingResourcesOperativeUnitTotal(request: IRequest): Promise<OperativeUnitWithTotalAllocationCostDto> {
    return this.apiHttpClient
      .request<null, OperativeUnitWithTotalAllocationCostDto>(
        HttpRequest.create({
          url: this.resourceUrl + "/providing-resources/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(OperativeUnitWithTotalAllocationCostDto, result));
  }
}
